import type { RenderTextBox } from '@lite-workspace/shared/canvas-engine/core';
import { TEXT_LAYOUT_GLYPH_GRID, TEXT_LAYOUT_GLYPH_GRID_AREA, textLayoutAtLeast } from '@lite-workspace/text-layout';
import { caretGridDiverges, caretLineForIndex, type CaretLineSpan } from './area-caret-layout';
import type { TextFrameFit } from './glyph-layout-backend';
import type { TextShaperLike } from './glyph-shaper';
import type { ShapedCaretBand, ShapedCaretLayout } from './overlay-text-edit';
import type { MappedFace } from './text-scene-adapter';

/** `[top.x, top.y, bottom.x, bottom.y]`, book px from the box rect's top-left, before the box matrix. */
export type EngineCaretStop = readonly [number, number, number, number];

/** One laid-out line of the engine's caret grid (`TextShaper.caret_geometry`, Rust `caret_geometry.rs`). */
export interface EngineCaretLine {
  charStart: number;
  charEnd: number;
  /** One stop per string index `charStart..=charEnd`, after Warp Text. */
  stops: readonly EngineCaretStop[];
  /** Warped text: the stop at the middle of each cell, so a band bends inside its glyph. */
  mids?: readonly EngineCaretStop[];
}

export interface EngineCaretGeometry {
  lines: readonly EngineCaretLine[];
}

/** Book px → frame-local px on each axis. */
export interface EngineCaretFrame {
  kx: number;
  ky: number;
}

interface Point {
  x: number;
  y: number;
}

/** Box states kept: the session's current text plus the few a selection drag or a re-entry asks again. */
const CARET_CACHE_SIZE = 4;

/**
 * The engine caret grid of a box, from the main-thread `TextShaper` (the fonts its registry holds under `refOf`).
 * Keyed by the box's own wire form — its version: a frame that re-asks an unchanged box costs a string compare,
 * only a real edit lays the text out again. `undefined` on a pkg without the export or a box without a face.
 */
export function engineCaretSource(
  shaper: TextShaperLike,
  refOf: (box: RenderTextBox) => string | undefined,
  mappedFaceOf: (box: RenderTextBox) => MappedFace | undefined,
): ((box: RenderTextBox) => EngineCaretGeometry | undefined) | undefined {
  const layout = shaper.caret_geometry?.bind(shaper);
  if (!layout) return undefined;
  const cache = new Map<string, EngineCaretGeometry | undefined>();
  return box => {
    const fontRef = refOf(box);
    if (!fontRef) return undefined;
    const key = JSON.stringify({ ...box, fontRef });
    const face = JSON.stringify(mappedFaceOf(box) ?? null);
    if (cache.has(key + face)) return cache.get(key + face);
    const geometry = (JSON.parse(layout(key, face)) as EngineCaretGeometry | null) ?? undefined;
    if (cache.size >= CARET_CACHE_SIZE) cache.delete(cache.keys().next().value as string);
    cache.set(key + face, geometry);
    return geometry;
  };
}

/**
 * Text with whole glyph widths — point text from `TEXT_LAYOUT_GLYPH_GRID`, area text from
 * `TEXT_LAYOUT_GLYPH_GRID_AREA`: the engine may set it on Photoshop's pixel grid (whole typed-px steps, Rust
 * `glyph_layout/grid.rs`), which the textarea's fractional pen never follows. Wider than the grid's own condition on
 * purpose: the engine caret is right for every such box, on the grid or off it.
 */
const onGlyphGrid = (box: RenderTextBox): boolean =>
  textLayoutAtLeast(box.textLayout, box.textMode === 'block' ? TEXT_LAYOUT_GLYPH_GRID_AREA : TEXT_LAYOUT_GLYPH_GRID) &&
  box.useFractionalGlyphWidths === false;

/**
 * Boxes whose caret the engine grid owns on the build_scene path: everything the textarea cannot reproduce,
 * plus Warp Text, a pinned first baseline and the glyph grid, which only the engine's layout knows.
 */
export function engineCaretNeeded(box: RenderTextBox): boolean {
  if (box.textPath?.d || box.orientation === 'vertical') return false;
  return (
    caretGridDiverges(box) || !!box.warp || box.firstBaselineOffset !== undefined || !!box.firstBaselineRule || onGlyphGrid(box)
  );
}

/**
 * The frame fit of `box` as the canvas lays it out. Area text from `TEXT_LAYOUT_GLYPH_GRID_AREA` with whole glyph widths
 * may wrap on the engine's glyph grid — by the sum of its whole typed-px steps, which the TS layout twin does not know —
 * so the twin can count a line fewer or more than the canvas draws. Such a box takes the engine's line count (its caret
 * grid, `geometry`, asked only for these boxes), and its ink height moves by the engine's line pitch per line the twin
 * miscounted. Every other box keeps the twin's fit.
 */
export function engineFrameFit(
  box: RenderTextBox,
  fit: TextFrameFit,
  geometry: () => EngineCaretGeometry | undefined,
): TextFrameFit {
  if (box.textMode !== 'block' || !onGlyphGrid(box) || fit.lines === 0) return fit;
  const lines = geometry()?.lines;
  if (!lines?.length || lines.length === fit.lines) return fit;
  const top = (i: number): number => lines[i].stops[0]?.[1] ?? 0;
  const pitch = lines.length > 1 ? (top(lines.length - 1) - top(0)) / (lines.length - 1) : box.lineHeight;
  return { ...fit, heightOver: fit.heightOver + (lines.length - fit.lines) * pitch, lines: lines.length };
}

const holdsCaret = (lines: readonly EngineCaretLine[], i: number): boolean => {
  const line = lines[i];
  const next = lines[i + 1];
  return line.charEnd > line.charStart || next === undefined || next.charStart > line.charStart;
};

function distanceToSegment(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  const t = len2 > 0 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2)) : 0;
  return Math.hypot(p.x - a.x - t * dx, p.y - a.y - t * dy);
}

// Same caret direction and height: the two cells lie on one straight line band.
const sameColumn = (a: EngineCaretStop, b: EngineCaretStop): boolean =>
  Math.abs(a[1] - b[1]) < 1e-3 && Math.abs(a[3] - b[3]) < 1e-3 && Math.abs(a[2] - a[0] - (b[2] - b[0])) < 1e-3;

/** The engine grid on the overlay's terms: frame-local px; carets stand on the baseline normal, bands follow the bend. */
export function engineShapedCaret(geometry: EngineCaretGeometry, frame: EngineCaretFrame): ShapedCaretLayout {
  const lines = geometry.lines;
  const spans: CaretLineSpan[] = lines.map((l, i) => ({
    charStart: l.charStart,
    charEnd: l.charEnd,
    holdsCaret: holdsCaret(lines, i),
  }));
  const top = (s: EngineCaretStop): Point => ({ x: s[0] * frame.kx, y: s[1] * frame.ky });
  const bottom = (s: EngineCaretStop): Point => ({ x: s[2] * frame.kx, y: s[3] * frame.ky });
  const stopAt = (lineIndex: number, index: number): EngineCaretStop | undefined => {
    const line = lines[lineIndex];
    return line?.stops[Math.max(0, Math.min(index - line.charStart, line.stops.length - 1))];
  };

  const usable = (): number[] => {
    const out = spans.flatMap((s, i) => (s.holdsCaret ? [i] : []));
    return out.length > 0 ? out : lines.map((_, i) => i);
  };

  const lineFor = (index: number): number => caretLineForIndex({ lines: spans }, index);

  const nearestOnLine = (lineIndex: number, p: Point): { index: number; distance: number } => {
    const line = lines[lineIndex];
    let best = { index: line.charStart, distance: Infinity };
    line.stops.forEach((s, k) => {
      const distance = distanceToSegment(p, top(s), bottom(s));
      if (distance < best.distance) best = { index: line.charStart + k, distance };
    });
    return best;
  };

  const bandsFor = (from: number, to: number): ShapedCaretBand[] => {
    const lo = Math.min(from, to);
    const hi = Math.max(from, to);
    const bands: ShapedCaretBand[] = [];
    lines.forEach((line, i) => {
      if (!spans[i].holdsCaret) return;
      const a = Math.max(lo, line.charStart) - line.charStart;
      const b = Math.min(hi, line.charEnd) - line.charStart;
      let k = a;
      while (k < b) {
        let end = k + 1;
        while (end < b && sameColumn(line.stops[k], line.stops[end])) end += 1;
        const [s0, s1] = [line.stops[k], line.stops[end]];
        const mid = end === k + 1 ? line.mids?.[k] : undefined;
        const outline = mid
          ? [top(s0), top(mid), top(s1), bottom(s1), bottom(mid), bottom(s0)]
          : [top(s0), top(s1), bottom(s1), bottom(s0)];
        const xs = outline.map(p => p.x);
        const ys = outline.map(p => p.y);
        const [x, y] = [Math.min(...xs), Math.min(...ys)];
        bands.push({ x, top: y, width: Math.max(...xs) - x, height: Math.max(...ys) - y, outline });
        k = end;
      }
    });
    return bands;
  };

  return {
    slotAt: index => {
      const s = stopAt(lineFor(index), index);
      if (!s) return undefined;
      const a = top(s);
      const b = bottom(s);
      return { x: a.x, top: a.y, height: Math.hypot(b.x - a.x, b.y - a.y), angle: Math.atan2(a.x - b.x, b.y - a.y) };
    },
    indexAt: (x, y) => {
      let best = { index: 0, distance: Infinity };
      for (const i of usable()) {
        const hit = nearestOnLine(i, { x, y });
        if (hit.distance < best.distance) best = hit;
      }

      return best.index;
    },
    indexByLineStep: (index, delta, desiredX) => {
      const order = usable();
      const target = order[order.indexOf(lineFor(index)) + delta];
      if (target === undefined) return undefined;
      const from = stopAt(lineFor(index), index);
      const x = desiredX ?? (from ? top(from).x : 0);
      const line = lines[target];
      const s = line.stops;
      // The column on the target line's own baseline, so a bent line keeps the x the caret left from.
      const mid = s[Math.floor(s.length / 2)];
      return nearestOnLine(target, { x, y: mid ? (top(mid).y + bottom(mid).y) / 2 : 0 }).index;
    },
    lineEdge: (index, edge) => {
      const line = lines[lineFor(index)];
      return line ? (edge === 'start' ? line.charStart : line.charEnd) : 0;
    },
    bandsFor,
  };
}
